import { useSyncExternalStore, useEffect, lazy, Suspense } from 'react';
import { HeadContent, Outlet, Scripts, createRootRoute, useRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { NotFoundPage } from '@/components/NotFound';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { FeedbackDialog } from '@/components/feedback/FeedbackDialog';
import appCss from '../styles.css?url';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { captureException } from '@/config/sentry';
import { isStaleChunkError } from '@/lib/staleChunk';
import { SITE_URL, ORGANIZATION_ID, WEBSITE_ID, WEBAPP_ID } from '@/lib/structured-data';
import { TriangleAlertIcon, RefreshCwIcon } from 'lucide-react';

const LazyDevPanel =
  import.meta.env.VITE_DEV_PANEL === 'true' ?
    lazy(() => import('@/components/dev/DevPanel').then(m => ({ default: m.DevPanel })))
  : null;

// The e2e/console seeding seam rides the same flag (window.__devSeed).
if (import.meta.env.VITE_DEV_PANEL === 'true' && typeof window !== 'undefined') {
  void import('@/dev/expose');
}

const emptySubscribe = () => () => {};

/** Render children only on the client, never during SSR. */
function ClientOnly({ children }: { children: React.ReactNode }) {
  const isClient = useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );
  return isClient ? children : null;
}

const IMAGE_URL = `${SITE_URL}/landing_preview.png`;

// Only assert what is verifiable: there are no public social profiles to list
// under sameAs, and no ratings/reviews exist, so no aggregateRating is claimed.
const structuredData = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': ORGANIZATION_ID,
      name: 'Syntch LLC',
      alternateName: 'CoRATES',
      url: SITE_URL,
      logo: `${SITE_URL}/web-app-manifest-512x512.png`,
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'support@corates.org',
        contactType: 'customer service',
      },
    },
    {
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      name: 'CoRATES',
      alternateName: 'Collaborative Research Appraisal Tool for Evidence Synthesis',
      url: SITE_URL,
      inLanguage: 'en-US',
      publisher: { '@id': ORGANIZATION_ID },
    },
    {
      // WebApplication is a subtype of SoftwareApplication, so a separate
      // SoftwareApplication node would only duplicate this entity.
      '@type': 'WebApplication',
      '@id': WEBAPP_ID,
      name: 'CoRATES',
      url: SITE_URL,
      isPartOf: { '@id': WEBSITE_ID },
      applicationCategory: 'EducationalApplication',
      applicationSubCategory: 'Research Tool',
      description:
        'Collaborative Research Appraisal Tool for Evidence Synthesis. Streamlines quality and risk-of-bias appraisal with real-time collaboration, automatic scoring, and visual summaries.',
      operatingSystem: 'Any',
      browserRequirements: 'Requires a modern web browser',
      featureList: [
        'RoB 2 risk-of-bias appraisal',
        'ROBINS-I risk-of-bias appraisal',
        'AMSTAR 2 systematic review appraisal',
        'Double coding and consensus reconciliation',
        'Automatic rule-based scoring',
        'Visual risk-of-bias summaries',
      ],
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Free plan available',
      },
      provider: { '@id': ORGANIZATION_ID },
    },
  ],
});

export const Route = createRootRoute({
  headers: () => ({
    'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
    'X-Frame-Options': 'DENY',
    'X-Content-Type-Options': 'nosniff',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
    'Content-Security-Policy': [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline' 'wasm-unsafe-eval' https://analytics.corates.org https://apis.google.com",
      "style-src 'self' 'unsafe-inline'",
      `img-src 'self' data: blob:${import.meta.env.DEV ? ' http://localhost:*' : ''}`,
      import.meta.env.DEV ?
        "connect-src 'self' http://localhost:* ws://localhost:* https://api.crossref.org https://eutils.ncbi.nlm.nih.gov https://api.unpaywall.org https://analytics.corates.org https://*.ingest.us.sentry.io"
      : "connect-src 'self' wss://corates.org https://api.crossref.org https://eutils.ncbi.nlm.nih.gov https://api.unpaywall.org https://analytics.corates.org https://*.ingest.us.sentry.io",
      "worker-src 'self' blob:",
      "font-src 'self'",
      'frame-src https://docs.google.com',
      "frame-ancestors 'none'",
      "form-action 'self'",
      "base-uri 'self'",
    ].join('; '),
  }),
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      {
        title: 'CoRATES - Collaborative Research Appraisal Tool for Evidence Synthesis',
      },
      {
        name: 'description',
        content:
          'CoRATES streamlines quality and risk-of-bias appraisal with intuitive workflows, real-time collaboration, automatic scoring, and clear visual summaries for evidence synthesis.',
      },
      { name: 'robots', content: 'index,follow,max-image-preview:large' },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'CoRATES' },
      {
        property: 'og:title',
        content: 'CoRATES - Collaborative Research Appraisal Tool for Evidence Synthesis',
      },
      {
        property: 'og:description',
        content:
          'CoRATES streamlines quality and risk-of-bias appraisal with intuitive workflows, real-time collaboration, automatic scoring, and clear visual summaries for evidence synthesis.',
      },
      { property: 'og:url', content: SITE_URL },
      { property: 'og:image', content: IMAGE_URL },
      { property: 'og:image:alt', content: 'CoRATES product screenshot' },
      { property: 'og:image:width', content: '2524' },
      { property: 'og:image:height', content: '1770' },
      { property: 'og:locale', content: 'en_US' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'theme-color', content: '#ffffff' },
      { name: 'twitter:image', content: IMAGE_URL },
      { name: 'twitter:image:alt', content: 'CoRATES product screenshot' },
    ],
    links: [
      { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
      { rel: 'icon', href: '/favicon.ico', sizes: '48x48' },
      {
        rel: 'icon',
        href: '/favicon-96x96.png',
        sizes: '96x96',
        type: 'image/png',
      },
      { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
      { rel: 'manifest', href: '/site.webmanifest' },
      { rel: 'stylesheet', href: appCss },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: structuredData,
      },
      {
        children:
          'window.plausible=window.plausible||function(){(plausible.q=plausible.q||[]).push(arguments)}',
      },
    ],
  }),

  component: RootLayout,
  shellComponent: RootDocument,
  notFoundComponent: NotFoundPage,
  errorComponent: RootError,
});

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang='en'>
      <head>
        <HeadContent />
        {/* Kept out of head(): the router re-appends src scripts on navigation, and every re-run adds a pageview listener.
            Legacy script.js because pa-*.js bakes in the personal-domain BASE_URL. */}
        <script
          src='https://analytics.corates.org/js/script.js'
          defer
          data-domain='corates.org'
          data-api='https://analytics.corates.org/api/event'
        />
      </head>
      <body className='min-h-screen bg-white text-gray-900 antialiased'>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootError({ error }: ErrorComponentProps) {
  const router = useRouter();
  const staleChunk = isStaleChunkError(error);

  useEffect(() => {
    captureException(error, { component: 'RootError', action: 'render' });
  }, [error]);

  const message =
    import.meta.env.DEV ?
      error instanceof Error ?
        error.message
      : String(error)
    : 'An unexpected error occurred. Please try refreshing.';

  return (
    <div className='flex min-h-[50vh] items-center justify-center p-8'>
      <div className='border-border bg-card w-full max-w-sm rounded-xl border p-6 text-center shadow-sm'>
        <div className='bg-destructive/10 mx-auto mb-4 flex size-12 items-center justify-center rounded-full'>
          <TriangleAlertIcon className='text-destructive size-6' />
        </div>
        <h3 className='text-foreground mb-1 text-base font-semibold'>Something went wrong</h3>
        <p className='text-muted-foreground mb-5 text-sm'>{message}</p>
        <Button onClick={() => (staleChunk ? window.location.reload() : router.invalidate())}>
          <RefreshCwIcon className='size-3.5' />
          {staleChunk ? 'Reload page' : 'Try again'}
        </Button>
      </div>
    </div>
  );
}

function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Outlet />
          <Toaster />
          <FeedbackDialog />
          {LazyDevPanel && (
            <ClientOnly>
              <Suspense>
                <LazyDevPanel />
              </Suspense>
            </ClientOnly>
          )}
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
